import { useState, useRef } from 'react'
import {
  makeStyles,
  tokens,
  Button,
  Body1,
  Caption1,
  mergeClasses,
  Toast,
  ToastTitle,
  Toaster,
  useToastController,
  useId,
} from '@fluentui/react-components'
import { ArrowUpload24Regular, Dismiss24Regular } from '@fluentui/react-icons'
import { useCaseStore } from '../stores/caseStore'
import { useAuthStore } from '../stores/authStore'

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  dropZone: {
    border: `2px dashed ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: tokens.colorNeutralBackground1,
    transition: 'all 0.2s',
    ':hover': {
      border: `2px dashed ${tokens.colorBrandForeground1}`,
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  dropZoneActive: {
    border: `2px dashed ${tokens.colorBrandForeground1}`,
    backgroundColor: tokens.colorBrandBackground2,
  },
  icon: {
    fontSize: '48px',
    color: tokens.colorBrandForeground1,
    marginBottom: tokens.spacingVerticalM,
  },
  selectedFile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  fileInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    justifyContent: 'flex-end',
  },
})

interface VideoUploadProps {
  caseId: string
  onUploaded?: () => void
}

const ACCEPTED_VIDEO_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/x-msvideo',
  'video/x-matroska',
  'video/x-ms-wmv',
  '.mp4',
  '.mov',
  '.avi',
  '.mkv',
  '.wmv',
]

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

export function VideoUpload({ caseId, onUploaded }: VideoUploadProps) {
  const styles = useStyles()
  const addVideo = useCaseStore((s) => s.addVideo)
  const userId = useAuthStore((s) => s.userId)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const toasterId = useId('toaster')
  const { dispatchToast } = useToastController(toasterId)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    
    const files = Array.from(e.dataTransfer.files)
    const videoFile = files.find((file) => file.type.startsWith('video/'))
    
    if (videoFile) {
      setSelectedFile(videoFile)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  const handleConfirmUpload = () => {
    if (!selectedFile || !userId) return

    addVideo(caseId, selectedFile.name, selectedFile.size, userId)
    
    dispatchToast(
      <Toast>
        <ToastTitle>Upload queued - backend API pending</ToastTitle>
      </Toast>,
      { intent: 'success' }
    )

    setSelectedFile(null)
    onUploaded?.()
  }

  const handleCancel = () => {
    setSelectedFile(null)
  }

  return (
    <div className={styles.container}>
      <Toaster toasterId={toasterId} />
      
      {!selectedFile ? (
        <>
          <div
            className={mergeClasses(
              styles.dropZone,
              isDragging && styles.dropZoneActive
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleClick}
          >
            <ArrowUpload24Regular className={styles.icon} />
            <Body1>Drag and drop video file here, or click to browse</Body1>
            <Caption1>Supported formats: MP4, MOV, AVI, MKV, WMV</Caption1>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_VIDEO_TYPES.join(',')}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </>
      ) : (
        <>
          <div className={styles.selectedFile}>
            <div className={styles.fileInfo}>
              <Body1>{selectedFile.name}</Body1>
              <Caption1>{formatFileSize(selectedFile.size)}</Caption1>
            </div>
            <Button
              icon={<Dismiss24Regular />}
              appearance="subtle"
              onClick={handleCancel}
              aria-label="Remove file"
            />
          </div>
          <div className={styles.actions}>
            <Button appearance="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={handleConfirmUpload}>
              Confirm Upload
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
